import type { LogbookEntry } from '../types/logbook';
import { NOTE_TYPE_CODE } from '../types/logbook';
import type { UserRole } from '../types/user';

// FE-13 — presentación y permisos de UI de las novedades, en un solo sitio.
// Las cuatro superficies que las muestran (página de Novedades, expediente del viaje,
// modal del operador y monitoreo) comparten estas reglas.

/**
 * Color e icono por CÓDIGO de tipo, no por id ni por etiqueta.
 *
 * `code` es el único eje estable del catálogo: el `id` es un UUID distinto en
 * cada tenant y la `label` la puede renombrar un Administrador en cualquier
 * momento. Un tipo que el tenant haya inventado cae al neutro a propósito —
 * inventarle color a una taxonomía que no conocemos es pintar significado
 * donde no lo hay.
 */
const TIPO_COLORES: Record<string, string> = {
  NOTA: '#3B82F6',
  ACCIDENTE: '#EF4444',
  VARADA: '#F59E0B',
  SANCION: '#8B5CF6',
  RETRASO: '#0EA5E9',
  CAMBIO_CONDUCTOR: '#14B8A6',
  OTRA: '#6B7280',
};

const COLOR_NEUTRO = '#6B7280';

export function colorDeTipo(code: string): string {
  return TIPO_COLORES[code] ?? COLOR_NEUTRO;
}

export function esNota(entry: { entryTypeCode: string }): boolean {
  return entry.entryTypeCode === NOTE_TYPE_CODE;
}

/** Vehículo, conductor o viaje: de qué cuelga la novedad, en texto. */
export function asociacionTexto(entry: LogbookEntry): string {
  const partes: string[] = [];
  if (entry.vehiclePlate) partes.push(entry.vehiclePlate);
  else if (entry.vehicleId) partes.push('vehículo');
  if (entry.driverName) partes.push(entry.driverName);
  else if (entry.driverId) partes.push('conductor');
  if (entry.tripId) partes.push('viaje asignado');
  return partes.length > 0 ? partes.join(' · ') : 'sin asociación';
}

/**
 * `true` si la novedad se corrigió alguna vez. El backend NO escribe
 * `updatedAt` cuando una corrección no cambia nada —devuelve 200 y no anota—,
 * así que esta marca no produce falsos positivos.
 *
 * Se compara con tolerancia de un segundo: `createdAt` y `updatedAt` los pone
 * el mismo `INSERT`, pero nada garantiza que la representación textual sea
 * idéntica al milisegundo.
 */
export function fueCorregida(entry: LogbookEntry): boolean {
  if (!entry.updatedAt) return false;
  return Math.abs(Date.parse(entry.updatedAt) - Date.parse(entry.createdAt)) > 1000;
}

// ---------------------------------------------------------------------------
// Permisos de UI — PRODUCT.md §4.8, verificado contra
// backend/services/core/internal/console/scope.go (la matriz real).
//
// ⚠️ Esto decide qué BOTÓN se ve, nunca si la operación es legítima: el
// backend responde 403/404 y es él quien manda (contrato §7).
// ---------------------------------------------------------------------------

/** Administrador, Supervisor y Operador registran. El Ejecutivo mira y no toca. */
export function puedeRegistrar(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'supervisor' || role === 'operator';
}

/**
 * Mismo reparto que registrar. El "solo las propias" del Operador NO lo decide
 * este permiso sino su visibilidad: una novedad ajena le responde 404, así que
 * ni siquiera la tiene en pantalla para intentar corregirla.
 */
export function puedeCorregir(role: UserRole | undefined): boolean {
  return puedeRegistrar(role);
}

/** Ventana de edición del Operador — issue #150, decisión revisada 2026-09-17. */
const VENTANA_EDICION_OPERADOR_DIAS = 14;

/**
 * Además del rol (`puedeCorregir`), el Operador pierde la edición 14 días
 * después de registrar la novedad (reunión Mindor Dev Team, issue #150).
 * Admin/Supervisor no tienen esta ventana — ya pueden corregir con auditoría
 * sin plazo (`PRODUCT.md` §4.8).
 *
 * ⚠️ Es una restricción de UI, no de seguridad real: `PATCH
 * /logbook-entries/{id}` hoy solo declara `403` por rol, nunca por tiempo
 * transcurrido — el enforcement del lado del servidor es un hallazgo de
 * contrato escalado aparte (ver #158). Un Operador que llame la API directo
 * puede corregir igual pasada la ventana hasta que el backend lo aplique.
 */
export function dentroDeVentanaEdicion(e: LogbookEntry, role: UserRole | undefined): boolean {
  if (role !== 'operator') return true;
  const limite = Date.now() - VENTANA_EDICION_OPERADOR_DIAS * 24 * 60 * 60 * 1000;
  return Date.parse(e.createdAt) >= limite;
}

/**
 * Adjuntar una foto es REGISTRAR, no corregir (BE-70): ADR-007 dice que
 * adjuntar es añadir, nunca editar. Hoy los dos permisos tienen los mismos
 * roles, pero son decisiones distintas y conviene que el código lo diga.
 *
 * El Ejecutivo queda fuera de subir y **sí** puede ver y descargar: ver no
 * lleva permiso propio en §4.8, lo acota la consulta.
 */
export function puedeAdjuntar(role: UserRole | undefined): boolean {
  return puedeRegistrar(role);
}

/** La taxonomía es del tenant entero: solo el Administrador la toca. */
export function puedeGestionarCatalogo(role: UserRole | undefined): boolean {
  return role === 'admin';
}

/**
 * Traduce un `ApiError` de novedades a algo que una persona pueda leer.
 *
 * El `404` es el caso que más confunde si se cuenta mal: para el Operador que
 * pide una novedad ajena, el servidor responde 404 —no 403— a propósito, para
 * no confirmarle que existe. "No existe o está fuera de tu alcance" es la
 * traducción honesta de esa respuesta; decir "no tienes permiso" revelaría lo
 * que el backend se guarda.
 */
export function mensajeErrorNovedad(err: unknown, accion: 'registrar' | 'corregir' | 'catalogo' | 'adjuntar'): string {
  const status = (err as { status?: number })?.status;
  const detail = (err as { detail?: string })?.detail;

  if (status === 403) {
    if (accion === 'catalogo') return 'Solo un Administrador puede cambiar el catálogo de tipos.';
    if (accion === 'adjuntar') return 'No tienes permiso para adjuntar fotos a una novedad.';
    return 'No tienes permiso para registrar ni corregir novedades.';
  }
  if (status === 404) return 'La novedad, el vehículo, el conductor o el viaje no existe o está fuera de tu alcance.';
  if (status === 409) {
    // El 409 de adjuntar es el tope de fotos, no un código repetido, y el
    // servidor manda el detalle explicando que no se pueden borrar.
    if (accion === 'adjuntar') return detail ?? `Esta novedad ya tiene el máximo de fotos.`;
    return detail ?? 'Ya existe un tipo con ese código.';
  }
  if (status === 400) {
    // ⚠️ Pasarse de 10 MiB responde 400, no 413 — el contrato de BE-70 lo
    // declara así porque es lo que el core hace, y el `detail` trae el tamaño
    // y el límite. Por eso se prefiere el detalle del servidor al texto genérico.
    if (accion === 'adjuntar') return detail ?? 'La foto no se pudo subir: revisa el formato y que no pase de 10 MB.';
    return detail ?? 'Revisa los datos: el servidor los rechazó.';
  }
  return detail ?? 'No se pudo completar la operación. Inténtalo de nuevo.';
}

// ---------------------------------------------------------------------------
// Rango de consulta del timeline.
//
// ⚠️ A diferencia del de viajes, este rango NO filtra por solape de una
// ventana: filtra por `occurredAt`, el instante DECLARADO. Una varada de
// anteayer relatada hoy no sale en "Hoy" — sale en el día en que pasó, que es
// lo que el contrato promete y lo que un supervisor espera de un listado de novedades.
// ---------------------------------------------------------------------------

export type LogbookDateFilter = 'today' | '7days' | 'month' | 'all';

export const LOGBOOK_DATE_FILTERS: { id: LogbookDateFilter; label: string }[] = [
  { id: 'today', label: 'Hoy' },
  { id: '7days', label: 'Últimos 7 días' },
  { id: 'month', label: 'Este mes' },
  { id: 'all', label: 'Todas' },
];

export function rangoDeFiltro(filtro: LogbookDateFilter): { from?: string; to?: string } {
  if (filtro === 'all') return {};
  const inicio = new Date();
  const fin = new Date();
  fin.setHours(23, 59, 59, 999);

  if (filtro === 'today') {
    inicio.setHours(0, 0, 0, 0);
  } else if (filtro === '7days') {
    inicio.setDate(inicio.getDate() - 6);
    inicio.setHours(0, 0, 0, 0);
  } else {
    // El mes ENTERO, no hasta hoy: una novedad se puede declarar con fecha
    // futura (un mantenimiento programado que se anota antes), y cortar el
    // rango en hoy la escondería.
    inicio.setDate(1);
    inicio.setHours(0, 0, 0, 0);
    fin.setMonth(fin.getMonth() + 1, 0);
    fin.setHours(23, 59, 59, 999);
  }

  return { from: inicio.toISOString(), to: fin.toISOString() };
}
